import { LibraryCache } from 'base/library_cache';
import { ServiceHttpError } from 'base/service_error';
import { LOCALE_INFO } from 'base/ui/locales';
import { observable } from 'mobx';
import { z } from 'zod';
import { mapPartialJson } from '../../llm_output_parsers';
import { LLMProxyService } from '../../llm_proxy_service';
import {
  ElementInstance,
  ElementInstanceState,
  Page,
  Project,
  Section,
  Space,
} from '../model';
import { allSlugsInElementTree } from '../model_helpers';
import { selectionLocale, SelectionStore } from '../selection/store';
import { matchTextFields } from './component_content_mapping';
import {
  findElementReplacableTextContent,
  ReplaceableTextContent2,
} from './element_replacable_text_content';
import { TaskContext } from './task_log';

export class ComponentContentAi {
  readonly pending = observable.map<ElementInstance, Promise<void>>();

  constructor(
    private readonly selection: SelectionStore,
    private readonly library: LibraryCache,
    private readonly llm: LLMProxyService,
  ) {}

  async generateCopy(
    rootElement: ElementInstance,
    oldInstance: ElementInstanceState | undefined,
    section: Section,
    page: Page,
    ctx: TaskContext,
  ) {
    const existingTask = this.pending.get(rootElement);
    if (existingTask) {
      // No concurrent tasks allowed
      return existingTask;
    }
    const generate = async () => {
      const oldTextFieldsPromise =
        oldInstance && this.loadTextFields(oldInstance);
      const newTextFieldsRaw = (await this.loadTextFields(rootElement)).filter(
        // ignore blanks (e.g. text inputs with no placeholder)
        (t) => t.original.length > 0,
      );
      if (newTextFieldsRaw.length === 0) return;

      if (oldTextFieldsPromise) {
        const oldTextFields = await oldTextFieldsPromise;
        const match = matchTextFields(oldTextFields, newTextFieldsRaw);
        if (match != null) {
          for (const [from, to] of match.mapping.entries()) {
            const value = from.element.textOverrides.get(from.nodeId);
            value && to.element.textOverrides.set(to.nodeId, value);

            for (const [threadId, anchoring] of from.element.commentThreads) {
              if (
                anchoring.nodeId === from.nodeId ||
                // If it was anchored to this element, it needs to move somewhere.
                // Best to move it to a text node in the same element.
                anchoring.nodeId == null
              ) {
                to.element.commentThreads.set(threadId, {
                  ...anchoring,
                  nodeId: to.nodeId,
                });
              }
            }
          }
          if (!match.needsToGenerateCopy) {
            return; // nothing left to generate
          }
        }
      }

      const keepExistingCopy = oldTextFieldsPromise != null;
      const textFields = keepExistingCopy
        ? reorderTextFieldsExistingCopyFirst(newTextFieldsRaw)
        : newTextFieldsRaw;
      const pageSel = this.selection.factory.forPage(page);
      const locale = selectionLocale(pageSel, this.selection.project);
      const space = pageSel.space;
      const { inputJson, outputPrefix } = prepareLlmInput(
        textFields,
        section,
        page,
        space !== 'home' ? space : undefined,
        this.selection.project,
        { keepExistingCopy },
      );
      let backoffCount = 0;
      let backoffMs = 1_000;
      for (let tryCount = 0; tryCount < 3 && backoffCount < 8; tryCount++) {
        const removePending = new Map(
          textFields.map((t) => [
            t,
            this.selection.decorations.setComponentCopyPending(
              t.element,
              t.nodeId,
            ),
          ]),
        );
        try {
          const completion = this.llm.infer(
            'section-content-4',
            {
              inputJson,
              outputPrefix,
              language: LOCALE_INFO[locale].name,
              languageLOTE: locale.startsWith('en')
                ? undefined
                : LOCALE_INFO[locale].name,
            },
            ctx,
          );
          const jsonStream = mapPartialJson(completion, outputPrefix);
          let error: string | undefined = 'no result';
          for await (const partialJson of jsonStream) {
            const result = parsePartialLlmOutput(partialJson, textFields);
            error = result.error;
            ctx.buffer.run(() => {
              for (const [f, text] of result.textByField?.entries() || []) {
                if (!text.length) continue;
                f.element.textOverrides.set(f.nodeId, text);
              }
            });
            for (const t of result.finishedFields) removePending.get(t)?.();
          }
          if (error == null) return;
          console.error(`Component Content try ${tryCount} failed: ${error}`);
        } catch (e) {
          if (e instanceof ServiceHttpError && e.status === 429) {
            console.warn(
              `Component Content attempt ${backoffCount} backing off: ${e}`,
            );

            const backoffPromise = new Promise((resolve) =>
              setTimeout(resolve, backoffMs),
            );
            const abortPromise = new Promise((_, reject) => {
              ctx.abortSignal.addEventListener(
                'abort',
                () => {
                  reject(new Error('aborted'));
                },
                { once: true },
              );
            });

            await Promise.race([backoffPromise, abortPromise]);

            tryCount--; // Count retries due to backoff separately.
            backoffCount++;
            backoffMs = backoffMs * 2;
          } else {
            throw e;
          }
        } finally {
          for (const remove of removePending.values()) remove();
        }
      }
    };

    const promise = generate();
    this.pending.set(rootElement, promise);
    promise.finally(() => {
      this.pending.delete(rootElement);
    });
    return promise;
  }

  private async loadTextFields<
    EI extends ElementInstance | ElementInstanceState,
  >(instance: EI): Promise<ReplaceableTextContent2<EI>[]> {
    const allSlugs = allSlugsInElementTree(instance);
    await Promise.all(
      allSlugs.map((slug) => this.library.getPublicComponentJson(slug)),
    );
    return findElementReplacableTextContent(instance, this.library);
  }
}

function reorderTextFieldsExistingCopyFirst<
  R extends ReplaceableTextContent2<any>,
>(fields: readonly R[]) {
  const keep: R[] = [];
  const write: R[] = [];
  for (const t of fields) {
    const override = t.element.textOverrides.get(t.nodeId);
    (override != null ? keep : write).push(t);
  }
  if (keep.length === fields.length) {
    throw new Error('Every field has existing copy - why use the LLM?');
  }
  return [...keep, ...write];
}

function prepareLlmInput(
  textFields: readonly ReplaceableTextContent2<
    ElementInstance | ElementInstanceState
  >[],
  section: Section,
  page: Page,
  space: Space | undefined,
  project: Project,
  { keepExistingCopy }: { keepExistingCopy: boolean },
) {
  const inputJson = {
    // Replace an empty string with undefined, so that the key is omitted if there is no
    // sitemap prompt.
    'company description': space?.description || project.brief || undefined,
    'page name': page.name,
    'page additional instructions': page.description.trim()
      ? page.description
      : undefined,
    section: {
      category: section.name,
      outline: section.description,
      'input text fields': textFields.map((t, i) => {
        let placeholderText: string | undefined = t.original;
        if (t.original.toLowerCase().startsWith('"lorem ipsum')) {
          placeholderText = 'quote';
        } else if (t.original.toLowerCase().startsWith('lorem ipsum')) {
          placeholderText = undefined;
        }
        return {
          id: i + 1,
          'css classes': t.cssClassesForAi ? t.cssClassesForAi : 'text-normal',
          'max length': calculateMaxLength(t.original),
          'placeholder text': placeholderText,
        };
      }),
    },
  };

  let outputPrefix = `{"real text fields": [`;
  let numKeeping = 0;
  for (let i = 0; i < textFields.length; i++) {
    const t = textFields[i];
    const override = t.element.textOverrides.get(t.nodeId);
    const row = JSON.stringify({
      id: i + 1,
      'max length': inputJson.section['input text fields'][i]['max length'],
      text: override != null ? override : 'START',
    });
    if (keepExistingCopy && override != null) {
      numKeeping++;
      outputPrefix = outputPrefix + '\n    ' + row + ',';
    } else {
      outputPrefix = outputPrefix + '\n    ' + row.replace(/"START.*$/g, '');
      // Add up to the 1st field which doesn't have an override
      break;
    }
  }
  return {
    inputJson: JSON.stringify(inputJson),
    // 2023-11-02: Originally, outputPrefix was not sent.  We added it as part of the "keepExistingCopy" feature.  To minimize risk, we only use outputPrefix it in that case.  It could probably be sent all the time - we should verify it doesn't negatively affect output.
    outputPrefix: numKeeping ? outputPrefix : undefined,
  };
}

function calculateMaxLength(text: string): string {
  const numWords = text.split(' ').length;
  const numSentences = text.split('.').length - 1;
  const numParagraphs = text.split('\n').length;

  if (numParagraphs > 1 && numSentences > 2 && numWords > 10) {
    return `${numParagraphs} paragraphs`;
  } else if (numWords == 1) {
    return `1 word`;
  } else if (numWords <= 5) {
    return `${numWords} words`;
  } else if (numWords <= 15) {
    return `${numWords + 2} words`;
  } else if (numSentences === 1) {
    return `${numSentences} sentence`;
  } else {
    // if there are lots of short sentences we want to calculate the number of sentences off words
    // if we don't do this output is far too long in many cases.
    if (numSentences * 15 > numWords) {
      let newSentences = Math.floor(numWords / 15);
      return `${newSentences} sentence${newSentences > 1 ? 's' : ''}`;
    }
    return `${numSentences} sentences`;
  }
}

/** Parses the (possibly incomplete) output of the LLM. **/
function parsePartialLlmOutput(
  modelResult: unknown,
  fields: readonly ReplaceableTextContent2<ElementInstance>[],
): {
  error: string | undefined;
  textByField: Map<ReplaceableTextContent2<ElementInstance>, string>;
  finishedFields: Set<ReplaceableTextContent2<ElementInstance>>;
} {
  const finishedFields = new Set<ReplaceableTextContent2<ElementInstance>>();
  const textByField = new Map<
    ReplaceableTextContent2<ElementInstance>,
    string
  >();

  const schema = z.object({
    'real text fields': z.array(
      z.object({
        id: z.number().optional(),
        text: z.string().optional().default(''),
      }),
    ),
  });
  const parsed = schema.safeParse(modelResult);
  if (!parsed.success) {
    return {
      error: parsed.error.message,
      textByField: textByField,
      finishedFields: finishedFields,
    };
  }

  const { 'real text fields': realTextFields } = parsed.data;
  let lastField: ReplaceableTextContent2<ElementInstance> | undefined;
  for (const field of realTextFields) {
    const t = fields[(field.id || -1) - 1];
    if (t == null) continue;
    lastField = t;
    textByField.set(t, field.text);
    finishedFields.add(t);
  }
  lastField && finishedFields.delete(lastField);

  const remainingTextFields = fields.length - realTextFields.length;
  return {
    error: remainingTextFields > 0 ? 'incomplete' : undefined,
    textByField: textByField,
    finishedFields: finishedFields,
  };
}
